import Link from "next/link";
import type { FAQItem } from "../../components/FAQ";
import {
  API_PRICE_SUMMARY,
  formatUsd,
  IS_KLAP_FREE_FAQ,
  isKlapFreeAnswer,
  MAX_UPLOAD_HOURS,
  orList,
  PublicPlan,
  WATERMARK_ANSWER,
  YEARLY_DISCOUNT,
} from "../../const/publicPricing";
import {
  getPricingLimitSubtitle,
  type PricingLimitVariant,
} from "../../hooks/usePricingLimitVariant";

// The facts /pricing states in its FAQ, whose answers are in the HTML, so
// crawlers and AI answer engines read them without running scripts. Prices come
// from the `plans` passed in: the public prices on the server, the viewer's arm
// once it resolves.

// When a price or a stated fact on this page last changed.
export const PRICES_UPDATED_ON = "2026-11-04";

// Functions, because prices change on the client with the viewer's price arm.
export type PricingCopy = {
  title: string;
  subtitle: (variant: PricingLimitVariant) => string;
  tryForFree: string;
  faq: (plans: PublicPlan[]) => FAQItem[];
};

const updatedOnLabel = new Date(
  `${PRICES_UPDATED_ON}T00:00:00Z`,
).toLocaleDateString("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
  timeZone: "UTC",
});

const DISCOUNT_PERCENT = Math.round(YEARLY_DISCOUNT * 100);

const link = "text-violet-600 underline underline-offset-2";

const clipCount = (count: number) => count.toLocaleString("en-US");

export const pricingFaq = (plans: PublicPlan[]): FAQItem[] => {
  const prices = (pick: (plan: PublicPlan) => number) =>
    orList(plans.map((plan) => formatUsd(pick(plan))));
  return [
    {
      question: "How much does Klap cost?",
      answer: `Billed monthly, Klap costs ${prices((plan) => plan.monthlyPrice)} a month. Billed yearly, it costs ${prices((plan) => plan.yearlyPrice)} a year, which comes to ${prices((plan) => plan.yearlyMonthlyPrice)} a month. ${plans.map((plan) => `${plan.name} includes ${clipCount(plan.clipsPerMonth)} clips a month`).join(", ")}. All prices are in US dollars and were last updated on ${updatedOnLabel}.`,
    },
    {
      question: "What does every Klap plan include?",
      answer: `Every plan includes AI clipping, with a virality score, active-speaker reframing and animated captions on each clip. You also get 99+ languages, scheduling and publishing to TikTok, YouTube, Instagram, LinkedIn and Facebook, unlimited social accounts, post analytics, and an editor with b-roll and hook titles. Source videos can run up to ${MAX_UPLOAD_HOURS} hours, and exports have no watermark.`,
    },
    {
      question: IS_KLAP_FREE_FAQ.question,
      answer: isKlapFreeAnswer(plans),
    },
    {
      question: "What counts as a clip?",
      answer: `Each short Klap generates counts as one clip, and your clips refill every billing period. The length of your source video doesn't use up your plan, and a video can run up to ${MAX_UPLOAD_HOURS} hours. Yearly plans get the whole year's clips at once: ${orList(plans.map((plan) => clipCount(plan.clipsPerMonth * 12)))}.`,
    },
    {
      question: "Does Klap add a watermark?",
      answer: WATERMARK_ANSWER,
    },
    {
      question: "What is Klap's refund policy?",
      answer: (
        <>
          On a monthly plan, you can ask for a refund any time in your first
          month, and for up to your last 3 months in a row if you
          haven&apos;t used Klap. On a yearly plan, you get a full refund in
          your first month. After that, you get back 1/12 of the yearly price
          for each full month you haven&apos;t used Klap, up to your last 3 in a
          row. Cancel first, then take the refund yourself on your billing
          page, or ask support. The full terms are in{" "}
          <Link className={link} href="/terms-of-services#refund-policy">
            section 10 of our terms
          </Link>
          .
        </>
      ),
    },
    {
      question: "Can I cancel anytime?",
      answer:
        "Yes. Cancel on your billing page whenever you like, and your plan stays active until the end of the period you paid for.",
    },
    {
      question: "Can I switch plans or billing period?",
      answer:
        "Yes, on your billing page. A move to yearly billing starts right away, and a move from yearly to monthly starts when your current year ends.",
    },
    {
      question: "Is there a Klap discount or promo code?",
      answer: `Yearly billing is the official discount: ${DISCOUNT_PERCENT}% off the monthly price, with no code needed. Klap doesn't publish promo codes. If a creator you follow shares their own code, enter it at checkout on a monthly plan.`,
    },
    {
      question: "How much does the Klap API cost?",
      answer: (
        <>
          The{" "}
          <Link className={link} href="/api">
            Klap API
          </Link>{" "}
          is self-serve and priced separately from these plans:{" "}
          {API_PRICE_SUMMARY}.
        </>
      ),
    },
  ];
};

export const pricing: PricingCopy = {
  title: "Klap pricing",
  subtitle: getPricingLimitSubtitle,
  tryForFree: "Try for free. Cancel anytime.",
  faq: pricingFaq,
};
